import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Layout, showToast } from '@components/Layout';
import { useAuth } from '@context/AuthContext';
import { Button, FormInput } from '@components/UI';
import { useQuery } from '@tanstack/react-query';
import { trpc } from '@/api/trpc';
import { handleTrpcQuery } from '@/api/trpcResponseHandler';
import { buildAgreementRedirectPath } from '@/utils/agreements';

// Define an error type for API errors
interface ApiError {
    message?: string;
    errorMessages?: string[];
}

const DEFAULT_POST_SIGNUP_REDIRECT = '/user/portfolios';

const Register = () => {
    const [inviteCode, setInviteCode] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [password2, setPassword2] = useState('');
    const [hasAcceptedBetaAgreement, setHasAcceptedBetaAgreement] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const navigate = useNavigate();
    const { register, isAuthenticated, requiresTwoFactorSetup, hasPendingRequiredAgreements, pendingRequiredAgreements } = useAuth();

    const activeAgreementQuery = useQuery({
        ...trpc.auth.fetchActiveBetaAgreement.queryOptions(),
        retry: false,
        refetchOnWindowFocus: false
    });

    // If user becomes authenticated during the registration process, redirect to home
    // This will happen automatically after successful registration
    useEffect(() => {
        if (isAuthenticated) {
            const redirectTarget = hasPendingRequiredAgreements
                ? buildAgreementRedirectPath({
                      pendingRequiredAgreements,
                      redirectTarget: DEFAULT_POST_SIGNUP_REDIRECT
                  })
                : requiresTwoFactorSetup
                  ? `/user/enable2fa?redirect=${encodeURIComponent(DEFAULT_POST_SIGNUP_REDIRECT)}`
                  : DEFAULT_POST_SIGNUP_REDIRECT;

            void navigate(redirectTarget, { replace: true });
        }
    }, [hasPendingRequiredAgreements, isAuthenticated, navigate, pendingRequiredAgreements, requiresTwoFactorSetup]);

    useEffect(() => {
        if (!activeAgreementQuery.error) return;

        handleTrpcQuery(activeAgreementQuery, {
            fallbackErrorMessage: 'Failed to load the Beta Tester Agreement',
            showErrorToast: true
        });
    }, [activeAgreementQuery.error]);

    const validateRegistrationForm = () => {
        const normalizedInviteCode = inviteCode.trim();

        if (!normalizedInviteCode || !email || !password || !password2) {
            showToast('Please fill in all fields', 'error');
            return null;
        }

        if (password !== password2) {
            showToast('Passwords do not match', 'error');
            return null;
        }

        // Email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

        if (!emailRegex.test(email)) {
            showToast('Please enter a valid email address', 'error');
            return null;
        }

        // Password strength validation
        const hasUpperCase = /[A-Z]/.test(password);
        const hasLowerCase = /[a-z]/.test(password);
        const hasNumber = /\d/.test(password);
        const hasSpecial = /[!@#$%^&*(),.?":{}|<>]/.test(password);
        const isLongEnough = password.length >= 8;

        if (!hasUpperCase || !hasLowerCase || !hasNumber || !hasSpecial || !isLongEnough) {
            showToast('Password must be at least 8 characters and include uppercase, lowercase, number, and special character', 'error');
            return null;
        }

        if (!activeAgreementQuery.data?.agreement?.agreementVersion) {
            showToast('The Beta Tester Agreement is still loading. Please wait a moment and try again.', 'error');
            return null;
        }

        if (!hasAcceptedBetaAgreement) {
            showToast('You must agree to the Beta Tester Agreement to create an account.', 'error');
            return null;
        }

        return normalizedInviteCode;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        const normalizedInviteCode = validateRegistrationForm();

        if (!normalizedInviteCode) {
            return;
        }

        setIsSubmitting(true);

        try {
            // Register user using the AuthContext
            await register({
                email,
                password,
                password2,
                inviteCode: normalizedInviteCode,
                betaAgreementVersion: activeAgreementQuery.data?.agreement.agreementVersion ?? ''
            });

            showToast('Account created. Set up two-factor authentication to continue.', 'success');
        } catch (error: unknown) {
            console.error('Registration error:', error);

            const apiError = error as ApiError;

            // Display error message from API or a default message
            const errorMessage = apiError.message || 'Registration failed. Please try again later.';

            showToast(errorMessage, 'error');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Layout title="Project 314 - Register">
            <div className="container mx-auto mt-3 text-center">
                <h3 className="text-white mb-3">Register</h3>
                <div className="max-w-md mx-auto mt-2 p-4 bg-zinc-800 rounded-lg shadow-lg">
                    <form id="registrationForm" onSubmit={handleSubmit}>
                        <FormInput
                            required
                            className="bg-[#111213] text-[#7c8083] border-[#7c8083]"
                            helpText="Registration is currently invite-only."
                            id="inviteCode"
                            label="Invite Code"
                            maxLength={100}
                            name="inviteCode"
                            type="text"
                            value={inviteCode}
                            onChange={(e) => setInviteCode(e.target.value)}
                        />
                        <FormInput
                            required
                            className="bg-[#111213] text-[#7c8083] border-[#7c8083]"
                            id="email"
                            label="Email"
                            maxLength={100}
                            minLength={7}
                            name="email"
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                        />
                        <FormInput
                            required
                            className="bg-[#111213] text-[#7c8083] border-[#7c8083]"
                            helpText="Use at least 1 uppercase letter, 1 lowercase letter, 1 number, and 1 special character."
                            id="password"
                            label="Password"
                            maxLength={40}
                            minLength={6}
                            name="password"
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                        />
                        <FormInput
                            required
                            className="bg-[#111213] text-[#7c8083] border-[#7c8083]"
                            id="password2"
                            label="Confirm Password"
                            maxLength={40}
                            minLength={6}
                            name="password2"
                            type="password"
                            value={password2}
                            onChange={(e) => setPassword2(e.target.value)}
                        />
                        <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-4 text-left">
                            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-amber-200">Private Beta Acknowledgment</p>
                            <p className="mt-2 text-sm leading-6 text-zinc-200">
                                Project314 is experimental software. Data, signals, automations, backtests, and brokerage integrations can
                                be inaccurate or fail unexpectedly, and real money can be lost by deploying strategies through the platform.
                            </p>
                            {activeAgreementQuery.data?.agreement.summaryHighlights ? (
                                <ul className="mt-3 ml-5 list-disc space-y-2 text-sm leading-6 text-zinc-300">
                                    {activeAgreementQuery.data.agreement.summaryHighlights.slice(0, 3).map((highlight) => (
                                        <li key={highlight}>{highlight}</li>
                                    ))}
                                </ul>
                            ) : (
                                <p className="mt-3 text-sm text-zinc-400">Loading agreement summary…</p>
                            )}
                            <label className="mt-4 flex items-start gap-3 text-sm leading-6 text-zinc-200" htmlFor="registerBetaAgreement">
                                <input
                                    checked={hasAcceptedBetaAgreement}
                                    className="mt-1 h-4 w-4 rounded border-zinc-500 bg-zinc-900 text-blue-500 focus:ring-blue-500"
                                    id="registerBetaAgreement"
                                    type="checkbox"
                                    onChange={(e) => setHasAcceptedBetaAgreement(e.target.checked)}
                                />
                                <span>
                                    I have read and agree to the{' '}
                                    <Link className="text-blue-400 hover:text-blue-300 underline" to="/beta-tester-agreement">
                                        Beta Tester Agreement
                                    </Link>
                                    .
                                </span>
                            </label>
                        </div>
                        <div className="my-3">
                            <Button
                                disabled={isSubmitting || activeAgreementQuery.isLoading || !activeAgreementQuery.data?.agreement}
                                id="registerButton"
                                type="submit"
                                variant="primary"
                            >
                                {isSubmitting ? (
                                    <>
                                        <svg
                                            className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                            <path
                                                className="opacity-75"
                                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                                fill="currentColor"
                                            />
                                        </svg>
                                        Registering...
                                    </>
                                ) : (
                                    'Register'
                                )}
                            </Button>
                        </div>
                        <p className="text-xs leading-5 text-zinc-400">
                            By creating an account or using Project 314, you agree to the{' '}
                            <Link className="text-blue-400 hover:text-blue-300 underline" to="/beta-tester-agreement">
                                Beta Tester Agreement
                            </Link>
                            , the{' '}
                            <Link className="text-blue-400 hover:text-blue-300 underline" to="/terms-and-conditions">
                                Terms and Conditions
                            </Link>{' '}
                            and the{' '}
                            <Link className="text-blue-400 hover:text-blue-300 underline" to="/privacy-policy">
                                Privacy Policy
                            </Link>
                            .
                        </p>
                        <div className="mt-2">
                            <Link className="text-[#0275d8] hover:text-[#025aa5]" to="/login">
                                Already have an account? Login
                            </Link>
                        </div>
                    </form>
                </div>
            </div>
        </Layout>
    );
};

export default Register;
